// @flow

import React from 'react';
import type { Node } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { ViewInRoomButton } from '@riseart/art';
import { MediaQuery } from '@riseart/common';
import { art as ENUM_ART, analytics as ANALYTICS_ENUM } from 'Enum';
import { components as CONFIG_COMPONENTS } from 'Config';
import { FavoritesToggleMutation } from 'shared_data/providers/queries/FavoritesToggleMutation';
import { EnquireImagesForm } from 'shared_components/forms/enquire/Images';
import { Picture } from 'shared_components/common/artdirection/picture/Picture';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { GOOGLE_TAGMANAGER_ACTIONS } from 'shared_data/providers/google/tagmanager/Actions';
import { actionFactory } from 'shared_services/riseart/utils/Utils';
import { SimilarArtworksLink } from 'shared_components/arts/widgets/SimilarArtworksLink';

import { raScreenLg } from '@riseart/antd-provider/dist/website/variables.less';
import { artGalleryActionsCls } from './Actions.less';

/**
 * ArtGalleryActions
 *
 * @param {Object} props
 */
export const ArtGalleryActions = ({
  art,
  artworkPreview,
  viewInRoomOverlayHeader,
  canonicalURL,
  showWishlist = false,
  showEnquireImages = true,
  showVisuallySimilar = false,
  selectedFrame,
}: Object): Node => {
  const dispatch = useDispatch();
  const { id, width, height } = art;

  return (
    <div className={artGalleryActionsCls}>
      {artworkPreview ? (
        <ViewInRoomButton
          scaled
          previewConfig={CONFIG_COMPONENTS.art.viewInRoom.backgrounds.main}
          imageWidth={width}
          imageHeight={height}
          previewComponent={artworkPreview}
          overlayHeader={viewInRoomOverlayHeader}
          backgroundPicture={({ config, imgProps }) => (
            <FormattedMessage id="components.art.viewInRoomBkgAlt">
              {(alt: string) => (
                <Picture
                  type="viewInRoom.scaled"
                  artDirectionKey="inRoomBackground.scaled"
                  image={config.background}
                  alt={alt}
                  imgProps={{ ...imgProps }}
                />
              )}
            </FormattedMessage>
          )}
        >
          <span>
            <MediaQuery minWidth={raScreenLg}>
              {(isScreenLg) => (
                <FormattedMessage
                  id={
                    isScreenLg
                      ? 'components.art.details.viewInRoom'
                      : 'components.art.details.viewInRoomTruncated'
                  }
                >
                  {(viewInRoom: string) => viewInRoom}
                </FormattedMessage>
              )}
            </MediaQuery>
          </span>
        </ViewInRoomButton>
      ) : null}
      {showWishlist ? (
        <FavoritesToggleMutation artId={id} type="semitransparent">
          <FormattedMessage id="common.wishlist" />
        </FavoritesToggleMutation>
      ) : null}
      {showEnquireImages ? (
        <EnquireImagesForm
          artwork={art}
          artworkUrl={canonicalURL}
          selectedFrame={selectedFrame}
          trigger={ENUM_ART.enquire.trigger.TRIGGER_MORE_IMAGES}
        />
      ) : null}
      {showVisuallySimilar ? (
        <MediaQuery minWidth={raScreenLg}>
          {(isScreenLg) => (
            <FormattedMessage
              id={
                isScreenLg
                  ? 'components.art.similarArtworks'
                  : 'components.art.similarArtworksTruncated'
              }
            >
              {(findSimilarLabel: string) => (
                <SimilarArtworksLink
                  title={findSimilarLabel}
                  showText
                  url={UrlAssembler.artList({ search: { v: id } })}
                  onClick={() => {
                    dispatch(
                      actionFactory(GOOGLE_TAGMANAGER_ACTIONS.VISUAL_SEARCH_ADD, {
                        trigger: ANALYTICS_ENUM.visualSearch.trigger.ARTWORK_DETAIL,
                        artwork: art,
                      }),
                    );
                  }}
                />
              )}
            </FormattedMessage>
          )}
        </MediaQuery>
      ) : null}
    </div>
  );
};
